import { propertiesTypes } from '@/reports/types';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';

interface DirectionOptions {
  [key: string]: string;
}

/**
 * Grabs the latest partner value and returns price, confidenceScore, and lastDate.
 * @param marketValues Array of MarketValues used for partner values.
 * @returns Object with price, confidenceScore, and lastDate.
 * @example
 * import { LatestPartnerValue } from '@/utils/formatMarketValues/formatMarketValues';
 *
 * const marketValues: MarketValue[] = [...];
 *
 * // latestPartner = {
 * //  price: 100,
 * //  confidenceScore: 82,
 * //  lastDate: '12/21/2022'
 * // };
 * const latestPartner = latestPartnerValue(marketValues);
 */
export const latestPartnerValue = (
  marketValues: propertiesTypes.MarketValues
) => {
  const partner = hasEstimatedValues(marketValues) ? marketValues[0] : null;

  if (partner) {
    const price = partner.total ? partner?.total : null;
    const confidenceScore = partner.score ? partner.score : null;
    const lastDate = partner.date?.parsed
      ? formatDateFromReport(partner?.date?.full)
      : null;
    return {
      price,
      confidenceScore,
      lastDate,
    };
  }
};

export const hasEstimatedValues = (
  marketValues: propertiesTypes.MarketValues
) => {
  return marketValues?.length > 0;
};

/**
 * Uses the latest partner value, and calculates and returns price difference, direction of the difference, and percent change between latest partner value and last sold price
 * @param marketValues Array of MarketValues used for partner values.
 * @param lastSold Used to calculate the difference between partner value and last sold price.
 * @returns Object with difference, direction, and pct.
 * @example
 * import { priceChange } from '@/utils/formatMarketValues/formatMarketValues';
 *
 * const marketValues: MarketValue[] = [...];
 * const lastSoldPrice = 200;
 *
 * // priceChangeResult = {
 * //   difference: 100,
 * //   direction: down,
 * //   pct: 100
 * // };
 * const priceChangeResult = priceChange(marketValues, lastSoldPrice);
 */
export const priceChange = (
  marketValues: propertiesTypes.MarketValues,
  lastSold: number | null
) => {
  const price = latestPartnerValue(marketValues)?.price
    ? latestPartnerValue(marketValues)?.price
    : 0;
  if (price && lastSold) {
    let difference = price - lastSold;
    const direction = difference > 0 ? 'up' : 'down';
    difference = Math.abs(difference);
    const pct = Math.round((difference * 100) / lastSold);
    return {
      difference,
      direction,
      pct,
    };
  }
};
/**
 * Uses the `directionText` and returns either increase or decrease
 * @param direction Direction is used to return either increase or decrease.
 * @returns increase or decrease.
 * @example
 * import { directionText } from '@/utils/formatMarketValues/formatMarketValues';
 *
 * const directionValue = 'up';
 *
 * // direction = 'increase'
 * const direction = directionText(directionValue);
 */
export const directionText = (direction: string | undefined) => {
  const directionOptions: DirectionOptions = {
    up: 'increase',
    down: 'decrease',
  };
  const selectedDirection = direction ? directionOptions[direction] : null;
  return selectedDirection || null;
};
